import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { makeStyles } from '@material-ui/core/styles';
import { Container, Typography, TextField, Button, Grid, Paper, Box, CircularProgress } from '@material-ui/core';

import { AppState } from '../../redux';
import { OrderSummary } from '../../components/OrderSummary';
import { calcOrderProductsSubtotal } from '../../helpers/utils';
import { axiosClient } from '../../helpers/AxiosClient';
import { ShopMode } from '../../redux/types';
import { isProvinceExcluded, EXCLUDED_PROVINCE_ERROR_MESSAGE } from '../../constants/shipping';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(3),
  },
  submitButton: {
    marginTop: theme.spacing(3),
  },
  totals: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
}));

interface ShippingFormData {
  name: string;
  surname: string;
  address: string;
  city: string;
  postalCode: string;
  province: string;
  phone: string;
}

const DirectSaleCheckout: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { items } = useSelector((state: AppState) => state.directSaleCart);
  const [loading, setLoading] = useState(false);
  const [provinceError, setProvinceError] = useState<string | null>(null);
  const [formData, setFormData] = useState<ShippingFormData>({
    name: '',
    surname: '',
    address: '',
    city: '',
    postalCode: '',
    province: '',
    phone: '',
  });

  const handleChange = (field: keyof ShippingFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData({ ...formData, [field]: value });

    if (field === 'province' && value.length === 2) {
      if (isProvinceExcluded(value)) {
        setProvinceError(EXCLUDED_PROVINCE_ERROR_MESSAGE);
      } else {
        setProvinceError(null);
      }
    } else if (field === 'province' && value.length < 2) {
      setProvinceError(null);
    }
  };

  const isFormValid = () => {
    return Object.values(formData).every((value) => value.trim() !== '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      enqueueSnackbar('Compila tutti i campi', { variant: 'error' });
      return;
    }

    if (isProvinceExcluded(formData.province)) {
      enqueueSnackbar(EXCLUDED_PROVINCE_ERROR_MESSAGE, { variant: 'error' });
      return;
    }

    if (items.length === 0) {
      enqueueSnackbar('Il carrello è vuoto', { variant: 'error' });
      navigate('/cart');
      return;
    }

    setLoading(true);

    try {
      const response = await axiosClient.post('/stripe-checkout/create', {
        items: items.map((item) => ({
          id: item.id,
          quantity: item.quantity,
        })),
        shippingAddress: formData,
      });

      window.location.href = response.data.checkoutUrl;
    } catch (error: any) {
      setLoading(false);
      const errorMessage = error.response?.data?.error?.message || 'Errore nel creare il checkout';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Redirect se carrello vuoto
  if (items.length === 0) {
    navigate('/cart');
    return null;
  }

  const { subTotal } = calcOrderProductsSubtotal(items, false);

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Typography variant="h3" gutterBottom color="primary">
        Checkout Vendita Online
      </Typography>

      <Grid container spacing={3}>
        {/* Form Spedizione */}
        <Grid item xs={12} md={7}>
          <Paper className={classes.paper}>
            <Typography variant="h5" gutterBottom>
              Indirizzo di Spedizione
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nome"
                    value={formData.name}
                    onChange={handleChange('name')}
                    required
                    disabled={loading}
                    inputProps={{ autoComplete: 'given-name' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Cognome"
                    value={formData.surname}
                    onChange={handleChange('surname')}
                    required
                    disabled={loading}
                    inputProps={{ autoComplete: 'family-name' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Indirizzo"
                    value={formData.address}
                    onChange={handleChange('address')}
                    required
                    disabled={loading}
                    placeholder="Via, numero civico"
                    inputProps={{ autoComplete: 'street-address' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Città"
                    value={formData.city}
                    onChange={handleChange('city')}
                    required
                    disabled={loading}
                    inputProps={{ autoComplete: 'address-level2' }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="CAP"
                    value={formData.postalCode}
                    onChange={handleChange('postalCode')}
                    required
                    disabled={loading}
                    inputProps={{ autoComplete: 'postal-code' }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Provincia"
                    value={formData.province}
                    onChange={handleChange('province')}
                    required
                    disabled={loading}
                    placeholder="MI"
                    error={!!provinceError}
                    helperText={provinceError}
                    inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' }, autoComplete: 'address-level1' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Telefono"
                    value={formData.phone}
                    onChange={handleChange('phone')}
                    required
                    disabled={loading}
                    type="tel"
                    inputProps={{ autoComplete: 'tel' }}
                  />
                </Grid>
              </Grid>

              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={!isFormValid() || loading}
                className={classes.submitButton}
              >
                {loading ? <CircularProgress size={24} /> : 'Procedi al pagamento'}
              </Button>

              <Typography
                variant="caption"
                display="block"
                align="center"
                color="textSecondary"
                style={{ marginTop: 16 }}
              >
                Sarai reindirizzato a Stripe per completare il pagamento in sicurezza
              </Typography>
            </form>
          </Paper>
        </Grid>

        {/* Riepilogo Ordine */}
        <Grid item xs={12} md={5}>
          <Paper className={classes.paper}>
            <Typography variant="h5" gutterBottom>
              Riepilogo Ordine
            </Typography>

            <OrderSummary
              orderProducts={items}
              isUserLogged={true}
              partner={undefined}
              editable={false}
              shopMode={ShopMode.DirectSale}
            />

            <Box className={classes.totals}>
              <Typography variant="body2" color="textSecondary">
                Subtotale: €{subTotal.toFixed(2)}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DirectSaleCheckout;
